import { API_ENDPOINTS } from "@/constants/api";
import { http } from "@/lib/http-client";
import { unwrapData } from "@/lib/api-response";
import { downloadAuthenticatedFile } from "@/lib/download-blob";
import type {
  ContentEntry,
  ContentExportFormat,
  CreatePageRequest,
  PageVersionDetail,
  PageVersionSummary,
  RejectContentRequest,
} from "@/types/content";

const BULK_WRITE_TIMEOUT_MS = 120_000;
const PUBLISH_TIMEOUT_MS = 60_000;

export interface ContentListParams {
  contentType: string;
  page?: number;
  pageSize?: number;
  unassigned?: boolean;
  departmentId?: string;
  reviewStatus?: string;
  search?: string;
  status?: string;
  locale?: string;
  isNews?: string;
  authorId?: string;
  from?: string;
  to?: string;
  ids?: string;
}

export interface ContentPagination {
  page: number;
  pageSize: number;
  total: number;
  pageCount: number;
}

export interface ContentListResult {
  items: ContentEntry[];
  pagination: ContentPagination;
}

export interface PublishBulkResult {
  count: number;
  failed: number;
  results: Array<{ id: string; ok: boolean; reason?: string }>;
}

export const contentService = {
  async list(query: ContentListParams): Promise<ContentListResult> {
    const response = await http.get(API_ENDPOINTS.CONTENT.LIST, {
      params: query,
    });
    const data = unwrapData<{
      items?: ContentEntry[];
      pagination?: ContentPagination;
    }>(response);
    const items = Array.isArray(data?.items) ? data.items : [];
    return {
      items,
      pagination:
        data?.pagination ?? {
          page: 1,
          pageSize: items.length,
          total: items.length,
          pageCount: 1,
        },
    };
  },

  async get(
    contentType: string,
    id: string,
    options?: { includePreview?: boolean },
  ): Promise<ContentEntry> {
    const response = await http.get(
      API_ENDPOINTS.CONTENT.DETAIL(contentType, id),
      {
        params: options?.includePreview ? { include: "preview" } : undefined,
      },
    );
    return unwrapData<ContentEntry>(response);
  },

  async approve(contentType: string, id: string): Promise<void> {
    await http.post(API_ENDPOINTS.CONTENT.APPROVE(contentType, id));
  },

  async submit(contentType: string, id: string): Promise<void> {
    await http.post(API_ENDPOINTS.CONTENT.SUBMIT(contentType, id));
  },

  async publish(contentType: string, id: string): Promise<void> {
    await http.post(API_ENDPOINTS.CONTENT.PUBLISH(contentType, id), undefined, {
      timeout: PUBLISH_TIMEOUT_MS,
    });
  },

  async setDepartment(
    contentType: string,
    id: string,
    departmentId: string | null,
  ): Promise<void> {
    await http.patch(API_ENDPOINTS.CONTENT.SET_DEPARTMENT(contentType, id), {
      departmentId,
    });
  },

  async assignDepartmentBulk(
    contentType: string,
    ids: string[],
    departmentId: string | null,
  ): Promise<void> {
    await http.post(
      API_ENDPOINTS.CONTENT.ASSIGN_DEPARTMENT_BULK(contentType),
      { ids, departmentId },
      { timeout: BULK_WRITE_TIMEOUT_MS },
    );
  },

  async publishBulk(contentType: string, ids: string[]): Promise<PublishBulkResult> {
    const response = await http.post(
      API_ENDPOINTS.CONTENT.PUBLISH_BULK(contentType),
      { ids },
      { timeout: BULK_WRITE_TIMEOUT_MS },
    );
    return unwrapData<PublishBulkResult>(response);
  },

  async reject(
    contentType: string,
    id: string,
    payload?: RejectContentRequest,
  ): Promise<void> {
    await http.post(API_ENDPOINTS.CONTENT.REJECT(contentType, id), payload);
  },

  async exportFile(
    query: ContentListParams,
    format: ContentExportFormat = "csv",
  ): Promise<void> {
    const ext = format === "xlsx" ? "xlsx" : "csv";
    await downloadAuthenticatedFile(
      API_ENDPOINTS.CONTENT.EXPORT,
      { ...query, format },
      `${query.contentType}-export.${ext}`,
    );
  },

  async listVersions(
    contentType: string,
    id: string,
    page = 1,
    pageSize = 20,
  ): Promise<{ items: PageVersionSummary[]; pagination: ContentPagination }> {
    const response = await http.get(API_ENDPOINTS.CONTENT.VERSIONS(contentType, id), {
      params: { page, pageSize },
    });
    return unwrapData(response);
  },

  async getVersion(
    contentType: string,
    id: string,
    versionId: string,
  ): Promise<PageVersionDetail> {
    const response = await http.get(
      API_ENDPOINTS.CONTENT.VERSION(contentType, id, versionId),
    );
    return unwrapData<PageVersionDetail>(response);
  },

  async rollbackVersion(
    contentType: string,
    id: string,
    versionId: string,
  ): Promise<void> {
    await http.post(API_ENDPOINTS.CONTENT.ROLLBACK(contentType, id, versionId));
  },

  async create(contentType: string, payload: CreatePageRequest): Promise<ContentEntry> {
    const response = await http.post(API_ENDPOINTS.CONTENT.CREATE(contentType), payload);
    return unwrapData<ContentEntry>(response);
  },

  async delete(contentType: string, id: string): Promise<void> {
    await http.delete(API_ENDPOINTS.CONTENT.DETAIL(contentType, id));
  },
};
